import {useEffect, useState} from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Typography,
  notification,
} from 'antd';
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  EditOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {useHistory, useParams} from 'react-router-dom';
import moment from 'moment';
import {Status} from '../../graphql/API';
import {EnrollmentsService} from './EnrollmentsService';
import {EnrichedEnrollment} from './IEnrollmentsService';
import {CurrencyPriceSection} from './components/CurrencyPriceSection';
import {EnrollmentSummary} from './components/EnrollmentSummary';
import {useEnrollmentData} from './hooks/useEnrollmentData';
import type {NewUpdateEnrollmentInput} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';

const {Title, Text} = Typography;

const enrollmentsService = new EnrollmentsService();

const STEPS = [
  {title: 'Student', icon: <UserOutlined />},
  {title: 'Package', icon: <AppstoreOutlined />},
  {title: 'Schedule', icon: <CalendarOutlined />},
  {title: 'Payment', icon: <CreditCardOutlined />},
  {title: 'Review', icon: <CheckCircleOutlined />},
];

export const EnrollmentEdit = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const {enrollmentId} = useParams<{enrollmentId: string}>();

  const [enrollment, setEnrollment] = useState<EnrichedEnrollment>();
  const [isFetchingEnrollment, setIsFetchingEnrollment] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCustomPrice, setShowCustomPrice] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // ── Shared data hook (no students needed in Edit) ──
  const {packages, levels, grades, tests, curriculums, onTestSearch} =
    useEnrollmentData(false);

  // ── Load existing enrollment ──
  useEffect(() => {
    enrollmentsService
      .getEnrollmentById(enrollmentId)
      .then((enr: EnrichedEnrollment) => {
        setEnrollment(enr);

        const existingPrice = (enr as any).finalPrice;
        if (existingPrice?.price) setShowCustomPrice(true);

        form.setFieldsValue({
          packageId: (enr as any).package?._id || enr.package?.id,
          levelId: (enr as any).level?._id || enr.level?.id,
          gradeId: (enr as any).grade?._id || enr.grade?.id || enr.gradeId,
          testId: (enr as any).test?._id || enr.test?.id,
          placementStatus: enr.placementStatus,
          startDate: enr.startDate ? moment(enr.startDate) : undefined,
          endDate: enr.endDate ? moment(enr.endDate) : undefined,
          currency: existingPrice?.currency || 'EGP',
          finalPrice: existingPrice?.price,
          paymentMethod: (enr as any).paymentMethod || 'stripe',
          curriculumId: (enr as any).curriculumId,
          planId: (enr as any).planId,
          discountCodeId:
            (enr as any).discountCodeId?._id || (enr as any).discountCodeId?.id,
          partnerId: (enr as any).partnerId,
          paymentReferenceId: (enr as any).paymentReferenceId,
          dayShift: (enr as any).dayShift,
          isFree: !!(enr as any).isFree,
        });
        setIsFetchingEnrollment(false);
      })
      .catch(() => {
        notification.error({message: 'Failed to load enrollment'});
        setIsFetchingEnrollment(false);
      });
  }, [enrollmentId]);

  // ── Submit ──
  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const input: NewUpdateEnrollmentInput & {isFree?: boolean} = {
        id: enrollmentId,
        packageId: values.packageId || null,
        levelId: values.levelId || null,
        gradeId: values.gradeId || null,
        placementTestId: values.testId || null,
        placementStatus: values.placementStatus || null,
        startDate: values.startDate ? values.startDate.toString() : null,
        endDate: values.endDate ? values.endDate.toString() : null,
        curriculumId: values.curriculumId || null,
        paymentMethod: values.paymentMethod || null,
        discountCodeId: values.discountCodeId || null,
        partnerId: values.partnerId || null,
        paymentReferenceId: values.paymentReferenceId || null,
        isFree: !!values.isFree,
        finalPrice: {
          currency: values.currency || 'EGP',
          ...(showCustomPrice && values.finalPrice !== undefined
            ? {price: Number(values.finalPrice)}
            : {price: 0}),
        },
      };

      await enrollmentsService.updateEnrollment(input as any);

      notification.success({
        message: 'Enrollment Updated!',
        description: 'Changes have been saved successfully.',
        duration: 4,
      });

      setTimeout(() => history.push(`/enrollments/${enrollmentId}`), 1200);
    } catch (err: any) {
      if (err?.errorFields) return; // antd inline validation, already shown
      notification.error({
        message: 'Update Failed',
        description: err?.message || 'An unexpected error occurred.',
        duration: 6,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading state ──
  if (isFetchingEnrollment) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 80}}>
        <Spin size="large" tip="Loading enrollment..." />
      </div>
    );
  }

  const formValues = form.getFieldsValue(true);

  return (
    <div style={{padding: '24px 32px', maxWidth: 960, margin: '0 auto'}}>
      <Title level={2} style={{textAlign: 'center', marginBottom: 32}}>
        <EditOutlined style={{marginRight: 8}} />
        Edit Enrollment
      </Title>

      {/* Step indicator */}
      <Steps
        current={currentStep}
        items={STEPS}
        style={{marginBottom: 32}}
        onChange={setCurrentStep}
      />

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, all) => {
          if (all.packageId && currentStep === 1) setCurrentStep(2);
          if (all.startDate && currentStep === 2) setCurrentStep(3);
          if (all.paymentMethod && currentStep === 3) setCurrentStep(4);
        }}>
        {/* ── Student (read-only in edit) ── */}
        <Card
          title={
            <>
              <UserOutlined /> &nbsp; Student
            </>
          }
          style={{marginBottom: 16}}>
          <Form.Item label="Student Name">
            <Input disabled value={enrollment?.student?.name ?? '-'} />
          </Form.Item>
          <Form.Item label="Student ID">
            <Input
              disabled
              value={
                (enrollment?.student as any)?._id || enrollment?.student?.id
              }
              style={{fontFamily: 'monospace', fontSize: 12}}
            />
          </Form.Item>
        </Card>

        {/* ── Package & Level ── */}
        <Card
          title={
            <>
              <AppstoreOutlined /> &nbsp; Package &amp; Level
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="packageId"
                label="Package"
                rules={[{required: true, message: 'Please select a package'}]}>
                <Select
                  showSearch
                  placeholder="Select a package"
                  optionFilterProp="children">
                  {packages?.map(pkg => {
                    const id = (pkg as any)._id || pkg.id;
                    const name =
                      typeof pkg.name_i18n === 'string'
                        ? JSON.parse(pkg.name_i18n)['en']
                        : (pkg.name_i18n as any)?.en;
                    return (
                      <Select.Option value={id} key={id}>
                        {name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="curriculumId" label="Curriculum">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a curriculum (optional)"
                  optionFilterProp="children"
                  onChange={() => {
                    form.setFieldsValue({
                      levelId: undefined,
                      gradeId: undefined,
                    });
                  }}>
                  {curriculums?.map(curr => (
                    <Select.Option value={curr.id} key={curr.id}>
                      {curr.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="levelId" label="Level">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a level"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({gradeId: undefined})}>
                  {levels?.map(lvl => (
                    <Select.Option value={lvl.id!} key={lvl.id}>
                      {lvl.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="gradeId" label="Grade">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a grade"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({levelId: undefined})}>
                  {grades?.map(grd => (
                    <Select.Option value={grd.id!} key={grd.id}>
                      {grd.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="testId" label="Placement Test">
                <Select
                  showSearch
                  placeholder="Select a test (optional)"
                  onSearch={onTestSearch}
                  optionFilterProp="children">
                  {tests?.map(tst => {
                    const id = (tst as any)._id || tst.id;
                    return (
                      <Select.Option value={id} key={id}>
                        {tst.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="planId" label="Plan ID">
                <Input placeholder="Enter plan ID (optional)" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Schedule & Details ── */}
        <Card
          title={
            <>
              <CalendarOutlined /> &nbsp; Schedule & Details
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[
                  {required: true, message: 'Please select a start date'},
                ]}>
                <DatePicker style={{width: '100%'}} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endDate" label="End Date">
                <DatePicker style={{width: '100%'}} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dayShift" label="Day Shift">
                <Input type="number" placeholder="Enter day shift (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partnerId" label="Partner ID">
                <Input placeholder="Enter partner ID (optional)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isFree" label="Free Enrollment">
                <Select placeholder="Is Free?">
                  <Select.Option value={false}>No</Select.Option>
                  <Select.Option value={true}>Yes</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Payment ── */}
        <Card
          title={
            <>
              <CreditCardOutlined /> &nbsp; Payment
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="placementStatus" label="Placement Status">
                <Select placeholder="Select placement status">
                  <Select.Option value={Status.TODO}>TODO</Select.Option>
                  <Select.Option value={Status.DOING}>DOING</Select.Option>
                  <Select.Option value={Status.DONE}>DONE</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMethod" label="Payment Method">
                <Select>
                  <Select.Option value="stripe">Stripe</Select.Option>
                  <Select.Option value="cash">Cash</Select.Option>
                  <Select.Option value="bank_transfer">
                    Bank Transfer
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="discountCodeId" label="Discount Code ID">
                <Input placeholder="Enter discount code ID (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentReferenceId" label="Payment Reference ID">
                <Input placeholder="Enter payment reference (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <CurrencyPriceSection
            showCustomPrice={showCustomPrice}
            onToggleCustomPrice={setShowCustomPrice}
          />
        </Card>

        {/* ── Summary ── */}
        <Form.Item noStyle shouldUpdate>
          {() => (
            <Card style={{marginBottom: 24}}>
              <EnrollmentSummary
                formValues={form.getFieldsValue(true)}
                students={undefined}
                packages={packages}
                levels={levels}
                tests={tests}
                curriculums={curriculums}
                showCustomPrice={showCustomPrice}
                studentName={enrollment?.student?.name}
              />
            </Card>
          )}
        </Form.Item>

        {/* Actions */}
        <div style={{textAlign: 'center'}}>
          <Space>
            <Button size="large" onClick={() => history.goBack()}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<EditOutlined />}
              loading={isSubmitting}
              onClick={onSubmit}
              style={{minWidth: 160}}>
              Save Changes
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};
